import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Course {
  id: string
  course_code: string
  title: string
  category: string
  level: string
  format: string
  duration_mins: number
  modules: number
  is_mandatory: boolean
  rating: number
  enrollments: number
  completion_rate: number
  status: string
}

interface Enrollment {
  id: string
  employee: string
  department: string
  course_title: string
  status: string
  progress: number
  score: number | null
  due_date: string
}

export default function ELearningPage() {
  const [courses, setCourses] = useState<Course[]>([])
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [courseRes, enrollRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/elearning/courses'),
      fetch(API_BASE + '/elearning/enrollments'),
      fetch(API_BASE + '/elearning/statistics')
    ])
    if (courseRes.ok) setCourses((await courseRes.json()).courses || [])
    if (enrollRes.ok) setEnrollments((await enrollRes.json()).enrollments || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': case 'active': return 'bg-green-100 text-green-800'
      case 'in_progress': case 'enrolled': return 'bg-blue-100 text-blue-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'failed': case 'expired': case 'archived': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'advanced': return 'bg-purple-100 text-purple-800'
      case 'intermediate': return 'bg-blue-100 text-blue-800'
      default: return 'bg-green-100 text-green-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">E-Learning Platform</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Course</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Courses</div>
              <div className="text-2xl font-bold">{statistics.active_courses}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">In Progress</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.in_progress}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Completion Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.completion_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Score</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.avg_score}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Certs</div>
              <div className="text-2xl font-bold">{statistics.active_certifications}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Courses</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stats</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {courses.slice(0, 12).map(course => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{course.title}</div>
                      <div className="flex gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${getLevelColor(course.level)}`}>{course.level}</span>
                        {course.is_mandatory && <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">Required</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm capitalize">{course.category}</div>
                      <div className="text-xs text-gray-500">{course.modules} modules - {Math.round(course.duration_mins / 60)}h</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{course.enrollments} enrolled</div>
                      <div className="text-xs text-green-600">{course.completion_rate}% completion</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(course.status)}`}>{course.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Active Enrollments</h3>
            <div className="space-y-3">
              {enrollments.filter(e => e.status === 'in_progress').slice(0, 10).map(enrollment => (
                <div key={enrollment.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{enrollment.course_title}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(enrollment.status)}`}>{enrollment.progress}%</span>
                  </div>
                  <div className="text-xs text-gray-500">{enrollment.employee}</div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div className="bg-blue-600 h-1.5 rounded-full" style={{width: enrollment.progress + '%'}}></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Due: {enrollment.due_date}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
